import React, { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { Alert, Box, Button, Card, CheckboxField } from "@curves/ui-base";
import { Grid } from "@mui/material";
import { getRadioFieldResult, refIsRequiredError, isEmpty } from "@util/utils";
import { setCheckboxValue } from "@icoach/documents/tools";
import { PAYMENT_TYPE } from "@icoach/constants/";
import CreditCard from "@icoach/components/creditCard/CreditCard";
import useDefaultCreditCard from "@util/hook/useDefaultCreditCard";
import { PickItem } from "@icoach/healthyCyclePurchase/components/index";

const PaymentSection = forwardRef((props, ref) => {
	const { memberID, initialSource = {}, cycleOrder, errorModule, isSelected, onEffectTypeChange, pickItemRef, isShowNoDeductNow = false } = props;

	// 使用 Hook 管理付款常用信用卡
	const { formData, applyDefaultCreditCard } = useDefaultCreditCard(memberID, initialSource);

	const paymentCreditCardRef = useRef(null);
	const paymentNoDeductNowRef = useRef(null);

	// 處理付款錯誤資料
	const getPaymentError = useCallback(module => {
		const { payment, cardSignImg } = module || {};
		let paymentError = isEmpty(payment) ? {} : { ...payment };

		if (cardSignImg) {
			paymentError = {
				...paymentError,
				signImg: cardSignImg,
			};
		}

		return isEmpty(paymentError) ? null : paymentError;
	}, []);

	const getResult = useCallback(() => {
		let result = {
			paymentType: PAYMENT_TYPE.CREDITCARD,
		};

		if (paymentCreditCardRef.current && paymentCreditCardRef.current.getResult) {
			const creditData = paymentCreditCardRef.current.getResult();
			if (!isEmpty(creditData)) {
				const { signImg, ...other } = creditData;
				result.payment = other;
				result.cardSignImg = signImg;
			}
		}

		if (isShowNoDeductNow && paymentNoDeductNowRef.current && paymentNoDeductNowRef.current.getResult) {
			result.noDeductNow = getRadioFieldResult(paymentNoDeductNowRef.current.getResult(), "checked");
		}

		return result;
	}, [isShowNoDeductNow]);

	useImperativeHandle(
		ref,
		() => ({
			getResult,
			isError: () => {
				const refsToCheck = [paymentCreditCardRef];
				if (isShowNoDeductNow) {
					refsToCheck.push(paymentNoDeductNowRef);
				}
				return refIsRequiredError(...refsToCheck);
			},
		}),
		[getResult, isShowNoDeductNow],
	);

	// 判斷是否為變更頁面模式
	const isChangeMode = onEffectTypeChange !== undefined;

	// 如果沒有傳入 isSelected，預設為 true
	const shouldShow = isSelected !== undefined ? isSelected : true;

	return (
		<>
			<Card.CardHeader
				title={isChangeMode ? <PickItem title="付款資訊 (即時生效)" ref={pickItemRef} checked={isSelected} handleChange={onEffectTypeChange} /> : "付款資訊 (即時生效)"}
				className="bg-light-active px-4"
			/>
			{shouldShow && (
				<Card.CardContent key={formData.key} className="p-4">
					<Grid spacing={2} container>
						<Grid xs={12} item>
							<Box className="tool-btn-group">
								<Button color="gray" onClick={applyDefaultCreditCard}>
									套用常用信用卡
								</Button>
							</Box>
						</Grid>
						{isShowNoDeductNow && (
							<Grid xs={12} item>
								<CheckboxField key={cycleOrder?.noDeductNow} ref={paymentNoDeductNowRef} defaultChecked={setCheckboxValue(cycleOrder?.noDeductNow)}>
									<CheckboxField.Checkbox label={<span className="font-weight-bold">當月不補扣</span>} value={true} />
								</CheckboxField>
								<Alert severity="info" variant="filled" color="info" className="alert-justify-content-start">
									當月補扣，信用卡資訊變更將立即生效，並通知HC單位進行補扣。若有其他選項變更將會以變更生效日為主
								</Alert>
							</Grid>
						)}
						<Grid xs={12} item>
							<CreditCard ref={paymentCreditCardRef} defaultValue={formData.source} errorModule={getPaymentError(errorModule)} showSignature showCardRelation required />
						</Grid>
					</Grid>
				</Card.CardContent>
			)}
		</>
	);
});

PaymentSection.displayName = "PaymentSection";

export default PaymentSection;
